import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, LogOut, ChevronDown, Menu } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useFilters } from "../../context/FilterContext";
import { RANGES } from "../../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { Sheet, SheetContent } from "../ui/sheet";
import { SidebarNav, SidebarBrand } from "./Sidebar";

export default function Topbar() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { range, setRange, hub, setHub, hubs } = useFilters();
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const ask = (e) => {
    e.preventDefault();
    if (q.trim()) {
      nav(`/ask?q=${encodeURIComponent(q.trim())}`);
      setQ("");
    }
  };

  const isOperations = user?.role === "operations";

  return (
    <header className="h-16 sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6" data-testid="topbar">
      <button className="lg:hidden text-slate-500" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn">
        <Menu className="w-5 h-5" />
      </button>
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="p-0 w-64 flex flex-col">
          <SidebarBrand />
          <SidebarNav onNavigate={() => setMenuOpen(false)} />
        </SheetContent>
      </Sheet>

      {!isOperations && (
        <form onSubmit={ask} className="flex-1 max-w-xl relative">
          <Sparkles className="w-4 h-4 text-secondary absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ask your business anything…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:bg-white"
            data-testid="ask-input-topbar"
          />
        </form>
      )}

      <div className="flex items-center gap-2 ml-auto">
        {!isOperations && (
          <>
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger className="w-[130px] h-9 text-xs hidden sm:flex" data-testid="filter-range"><SelectValue /></SelectTrigger>
              <SelectContent>{RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={hub} onValueChange={setHub}>
              <SelectTrigger className="w-[130px] h-9 text-xs hidden sm:flex" data-testid="filter-hub"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hubs</SelectItem>
                {hubs.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-50" data-testid="user-menu">
            <div className="w-8 h-8 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs">
              {(user?.name || (isOperations ? "OP" : "SA")).slice(0, 2).toUpperCase()}
            </div>
            <div className="text-left hidden sm:block leading-tight">
              <div className="text-xs font-semibold text-slate-800">{user?.name || (isOperations ? "Operations" : "Super Admin")}</div>
              <div className="text-[10px] text-slate-400">{isOperations ? "Operations Team" : "Super Admin"}</div>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600">
              <LogOut className="w-4 h-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );

}
